export type LatLng = { lat: number; lng: number };
export type GeocodeLevel = "district" | "taluka" | "village";

const memoryCache = new Map<string, LatLng | null>();

// Bump this when the geocoding/fallback strategy changes, so browsers with a
// stale cached `null` (from before the fix) don't get stuck replaying it forever.
const CACHE_VERSION = "v2";

const cacheKey = (level: GeocodeLevel, id: number | string) => `${level}-${id}`;
const storageKey = (level: GeocodeLevel, id: number | string) =>
  `location-centroid:${CACHE_VERSION}:${level}:${id}`;

function readFromStorage(level: GeocodeLevel, id: number | string): LatLng | null | undefined {
  if (typeof window === "undefined") return undefined;
  try {
    const raw = window.localStorage.getItem(storageKey(level, id));
    if (raw == null) return undefined;
    return JSON.parse(raw) as LatLng | null;
  } catch {
    return undefined;
  }
}

function writeToStorage(level: GeocodeLevel, id: number | string, value: LatLng | null) {
  if (typeof window === "undefined") return;
  try {
    window.localStorage.setItem(storageKey(level, id), JSON.stringify(value));
  } catch {
    // storage unavailable/full — safe to ignore, falls back to in-memory cache only
  }
}

async function geocodeQuery(query: string): Promise<LatLng | null> {
  try {
    const res = await fetch(
      `https://nominatim.openstreetmap.org/search?q=${encodeURIComponent(query)}&format=json&limit=1`,
      { headers: { "Accept-Language": "en" } }
    );
    const data = await res.json();
    return Array.isArray(data) && data[0]
      ? { lat: parseFloat(data[0].lat), lng: parseFloat(data[0].lon) }
      : null;
  } catch {
    return null;
  }
}

// Resolves an approximate lat/lng for a district/taluka/village via forward
// geocoding (Nominatim), most-specific-first in `nameParts` (e.g.
// [villageName, talukaName, districtName, stateName]). Results are cached in
// memory + localStorage per (level, id) so repeat visits don't re-hit the API.
//
// Government/backend location names occasionally differ slightly from
// OpenStreetMap's own naming (e.g. "South Andamans" in our data vs. OSM's
// "South Andaman"), which makes the fully-qualified query fail even though
// the target name alone would resolve fine. To recover from that, we retry
// with progressively fewer qualifiers: full chain -> target + state only ->
// target name alone. Small villages/talukas may still not resolve at all —
// callers should treat null as "no marker for this one" rather than an error.
export async function getLocationCentroid(
  level: GeocodeLevel,
  id: number | string,
  nameParts: (string | null | undefined)[]
): Promise<LatLng | null> {
  const key = cacheKey(level, id);
  if (memoryCache.has(key)) return memoryCache.get(key)!;

  const stored = readFromStorage(level, id);
  if (stored !== undefined) {
    memoryCache.set(key, stored);
    return stored;
  }

  const clean = nameParts.filter((p): p is string => Boolean(p));
  const target = clean[0];
  const state = clean[clean.length - 1];

  const attempts = [
    [...clean, "India"].join(", "),
    target && state && state !== target ? `${target}, ${state}, India` : null,
    target ? `${target}, India` : null,
  ].filter((q): q is string => Boolean(q));

  let result: LatLng | null = null;
  for (const query of attempts) {
    result = await geocodeQuery(query);
    if (result) break;
  }

  memoryCache.set(key, result);
  writeToStorage(level, id, result);
  return result;
}
